import { apiClient, endpoints } from '../config';

/**
 * Service for review-related API calls
 */
export const reviewService = {
  /**
   * Get all reviews
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of reviews
   */
  getAllReviews: async (params = {}) => {
    const response = await apiClient.get(endpoints.reviews.list, { params });
    return response.data;
  },

    startReview: (reviewId) => {
      return apiClient.post(endpoints.reviews.start(reviewId));
    },

  /**
   * Get review by ID
   * @param {string} id - Review ID
   * @returns {Promise<Object>} - Review details
   */
  getReviewById: async (id) => {
    const response = await apiClient.get(endpoints.reviews.details(id));
    return response.data;
  },

  /**
   * Get reviews for a manuscript
   * @param {string} manuscriptId - Manuscript ID
   * @returns {Promise<Array>} - List of reviews for the manuscript
   */
  getManuscriptReviews: async (manuscriptId) => {
    const response = await apiClient.get(endpoints.reviews.manuscriptReviews(manuscriptId));
    return response.data;
  },

  /**
   * Assign one or more reviewers to a manuscript
   * @param {Object} assignmentData - Assignment data
   * @param {string} assignmentData.manuscriptId - Manuscript ID
   * @param {string} [assignmentData.reviewerId] - Single reviewer ID (for backward compatibility)
   * @param {string[]} [assignmentData.reviewerIds] - Array of reviewer IDs
   * @returns {Promise<Object>} - Object containing assigned reviews and any errors
   */
  assignReviewer: async (assignmentData) => {
    const response = await apiClient.post(endpoints.reviews.assign, assignmentData);
    return response.data;
  },

  /**
   * Submit a review
   * @param {string} reviewId - Review ID
   * @param {Object} reviewData - Review data
   * @param {number} reviewData.rating - Rating (1-5)
   * @param {string} reviewData.recommendation - Recommendation (accept, minor_revision, major_revision, reject)
   * @param {string} reviewData.comments - Comments to authors
   * @param {string} [reviewData.confidentialComments] - Confidential comments to editor
   * @param {string} [reviewData.response] - Response to author
   * @returns {Promise<Object>} - Submitted review
   */
  submitReview: async (reviewId, reviewData) => {
    const response = await apiClient.post(endpoints.reviews.submit(reviewId), reviewData);
    return response.data;
  },

  /**
   * Complete a review
   * @param {string} reviewId - Review ID
   * @returns {Promise<Object>} - Completed review
   */
  completeReview: async (reviewId, reviewData) => {
    const response = await apiClient.post(endpoints.reviews.complete(reviewId), reviewData);
    return response.data;
  },

  /**
   * Make the manuscript-level decision for the current review round
   * @param {string} manuscriptId - Manuscript ID
   * @param {Object} decisionData - Decision data
   * @param {string} decisionData.status - New manuscript status
   * @param {string} decisionData.comments - Comments to authors
   * @param {string} [decisionData.confidentialComments] - Confidential comments to editor
   * @param {string} [decisionData.response] - Response to author
   * @returns {Promise<Object>} - Updated review
   */
  makeDecision: async (manuscriptId, decisionData) => {
    const { files = [], ...fields } = decisionData;
    let payload = fields;
    let config;
    if (files.length > 0) {
      payload = new FormData();
      payload.append('decision', fields.decision);
      if (fields.comments) payload.append('comments', fields.comments);
      if (fields.revisionDeadline) payload.append('revisionDeadline', fields.revisionDeadline);
      payload.append('reviewerIds', JSON.stringify(fields.reviewerIds || []));
      files.forEach(file => payload.append('files', file));
      config = { headers: { 'Content-Type': 'multipart/form-data' } };
    }
    const response = await apiClient.post(endpoints.reviews.decision(manuscriptId), payload, config);
    return response.data;
  },

  /**
   * Remove a reviewer from a manuscript
   * @param {string} manuscriptId - Manuscript ID
   * @param {string} reviewerId - Reviewer ID to remove
   * @returns {Promise<Object>} - Response with success message
   */
  removeReviewer: async (manuscriptId, reviewerId) => {
    const response = await apiClient.delete(endpoints.reviews.removeReviewer(manuscriptId, reviewerId));
    return response.data;
  },

  /**
   * Get invitation details by token
   * @param {string} token - Invitation token
   * @returns {Promise<Object>} - Invitation details
   */
  getInvitation: async (token) => {
    const response = await apiClient.get(endpoints.reviews.getInvitation(token));
    return response.data;
  },

  /**
   * Accept a review invitation
   * @param {string} token - Invitation token
   * @returns {Promise<Object>} - Created review
   */
  acceptInvitation: async (token) => {
    const response = await apiClient.post(endpoints.reviews.acceptInvitation(token));
    return response.data;
  },

  /**
   * Decline a review invitation
   * @param {string} token - Invitation token
   * @returns {Promise<Object>} - Updated invitation
   */
  declineInvitation: async (token) => {
    const response = await apiClient.post(endpoints.reviews.declineInvitation(token));
    return response.data;
  },

  /**
   * Get invitations where email sending failed
   * @param {Object} params - Query parameters
   * @param {string} [params.manuscriptId] - Optional manuscript ID to filter by
   * @returns {Promise<Array>} - List of failed invitations
   */
  getFailedInvitations: async (params = {}) => {
    const response = await apiClient.get(endpoints.reviews.failedInvitations, { params });
    return response.data;
  },

  /**
   * Resend an invitation email
   * @param {string} invitationId - Invitation ID
   * @returns {Promise<Object>} - Updated invitation
   */
  resendInvitation: async (invitationId) => {
    const response = await apiClient.post(endpoints.reviews.resendInvitation(invitationId));
    return response.data;
  },

  /**
   * Get reviews assigned to the current reviewer
   * @param {Object} params - Query parameters
   * @param {string} [params.status] - Optional status filter
   * @returns {Promise<Array>} - List of reviews assigned to the current reviewer
   */
  getReviewerReviews: async (params = {}) => {
    const response = await apiClient.get(endpoints.reviews.reviewerReviews, { params });
    return response.data;
  },

  /**
   * Update the due date of a review
   * @param {string} reviewId - Review ID
   * @param {Object} data - Due date data
   * @param {Date} data.dueDate - New due date
   * @returns {Promise<Object>} - Updated review
   */
  updateDueDate: async (reviewId, data) => {
    const response = await apiClient.put(endpoints.reviews.updateDueDate(reviewId), data);
    return response.data;
  },

  updateStatus: async (reviewId, status) => {
    const response = await apiClient.put(endpoints.reviews.updateStatus(reviewId), { status });
    return response.data;
  },

  /**
   * Invite a non-system reviewer
   * @param {Object} invitationData - Invitation data
   * @param {string} invitationData.manuscriptId - Manuscript ID
   * @param {string} invitationData.reviewerEmail - Reviewer email
   * @param {string} invitationData.reviewerName - Reviewer name
   * @returns {Promise<Object>} - Created invitation
   */
  inviteNonSystemReviewer: async (invitationData) => {
    const response = await apiClient.post(endpoints.reviews.inviteNonUser, invitationData);
    return response.data;
  },

  /**
   * Forward a completed review to the manuscript author
   * @param {string} reviewId - Review ID
   * @param {Object} forwardData - Forwarding data
   * @param {string} forwardData.customMessage - Custom message from chief editor
   * @param {boolean} [forwardData.includeComments] - Include review comments
   * @param {boolean} [forwardData.includeConfidential] - Include confidential comments
   * @returns {Promise<Object>} - Updated review
   */
  forwardReview: async (reviewId, forwardData) => {
    const response = await apiClient.post(endpoints.reviews.forward(reviewId), forwardData);
    return response.data;
  },

  /**
   * Forward selected thread messages to author
   * @param {string} reviewId - ID of the review
   * @param {Object} forwardData - Forwarding data
   * @param {string} forwardData.customMessage - Custom message from chief editor
   * @param {Array<string>} forwardData.selectedCommentIds - Array of selected comment IDs
   * @returns {Promise<Object>} - Forwarding result
   */
  forwardThreadMessages: async (reviewId, forwardData) => {
    const response = await apiClient.post(endpoints.reviews.forwardMessages(reviewId), forwardData);
    return response.data;
  },

  /**
   * Get failed forwarding attempts
   * @param {Object} params - Query parameters
   * @param {string} [params.manuscriptId] - Optional manuscript ID to filter by
   * @param {string} [params.reviewId] - Optional review ID to filter by
   * @returns {Promise<Array>} - List of failed forwarding attempts
   */
  getFailedForwardingAttempts: async (params = {}) => {
    const response = await apiClient.get(endpoints.reviews.failedForwarding, { params });
    return response.data;
  },

  /**
   * Retry a failed forwarding attempt
   * @param {string} attemptId - Forwarding attempt ID
   * @returns {Promise<Object>} - Updated attempt
   */
  retryForwardingAttempt: async (attemptId) => {
    const response = await apiClient.post(endpoints.reviews.retryForwarding(attemptId));
    return response.data;
  }
};

export default reviewService;
